import { ReactNode, useEffect, useRef } from 'react';
import { CircularProgress, Skeleton } from '@mui/material';
import './DataTable.scss';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Cell content; defaults to the row's value for `key`. */
  render?: (row: T, index: number) => ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  skeleton?: ReactNode;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  /** While loading, an empty table shows a spinner and a filled one stays visible but dimmed. */
  loading?: boolean;
  emptyMessage?: ReactNode;
  className?: string;
  /** The table scrolls back to the top whenever this changes, e.g. the page number or search */
  scrollResetKey?: string | number;
  skeletonRowCount?: number;
}

/** Column-driven table. Data, paging and search stay with the caller, so it works with server-side pagination. */
const DataTable = <T,>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyMessage = 'No data found',
  className = '',
  scrollResetKey,
  skeletonRowCount = 0,
}: DataTableProps<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasRows = rows.length > 0;
  const showSkeleton = loading && !hasRows && skeletonRowCount > 0;

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [scrollResetKey]);

  return (
    <div className={`dataTable ${className}`} aria-busy={loading}>
      <div ref={scrollRef} className={`dataTableScroll${loading && hasRows ? ' dataTableScroll--refreshing' : ''}`}>
        <table className='dataTableTable'>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={{ width: column.width, textAlign: column.align }} className={column.className}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className='dataTableSkeletonRow' aria-hidden='true'>
                  {columns.map((column) => (
                    <td key={column.key} style={{ textAlign: column.align }} className={column.className}>
                      {column.skeleton ?? <Skeleton variant='text' width='60%' />}
                    </td>
                  ))}
                </tr>
              ))}
            {rows.map((row, index) => (
              <tr key={rowKey(row, index)}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{ textAlign: column.align }}
                    className={column.className}
                    // Lets stacked (phone) layouts show the column name next to the value
                    data-label={typeof column.header === 'string' ? column.header : undefined}
                  >
                    {column.render ? column.render(row, index) : ((row as Record<string, ReactNode>)[column.key] ?? '-')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!hasRows && !showSkeleton && <div className='dataTableState'>{loading ? <CircularProgress size={30} /> : emptyMessage}</div>}
      </div>
      {loading && hasRows && (
        <div className='dataTableOverlay'>
          <CircularProgress size={30} />
        </div>
      )}
    </div>
  );
};

export default DataTable;
